import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Tooltip } from "@mui/material";
import axiosInstance from "../../axiosinstance";
import DeedPreviewRenderer from "./components/DeedPreviewRenderer";
import "./Deeds.css";

const validatePartyFields = (partyFieldsList, answersMap) => {
  const missingByParty = {};
  if (!Array.isArray(partyFieldsList) || partyFieldsList.length === 0) {
    return { isAllValid: true, missingByParty };
  }
  partyFieldsList.forEach((party, index) => {
    const fields = Array.isArray(party?.fields) ? party.fields : [];
    const missingFields = fields
      .map((field) => {
        const value =
          answersMap?.[party?.party_instance_id]?.[field?.field_key];
        const validation = validateField(field, value);
        if (!validation.valid) {
          return field?.field_label || field?.field_key || "Unknown field";
        }
        return null;
      })
      .filter(Boolean);

    if (missingFields.length > 0) {
      const label =
        party?.party_label ||
        party?.party_group_key ||
        `Party ${index + 1}`;
      missingByParty[label] = missingFields;
    }
  });

  return {
    isAllValid: Object.keys(missingByParty).length === 0,
    missingByParty,
  };
};

const validateField = (field, rawValue) => {
  const label = field?.field_label || field?.field_key || "This field";
  const validations = field?.validations || {};
  const validationMeta = validations.validation || {};
  const value =
    typeof rawValue === "string" ? rawValue : rawValue ?? "";

  if (field?.is_mandatory) {
    if (
      value === "" ||
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim() === "")
    ) {
      if (field?.field_key === "pan") {
        return { valid: false, error: "PAN is required" };
      }
      return { valid: false, error: `${label} is required` };
    }
  }

  if (value !== "" && value !== null && value !== undefined) {
    if (field?.input_type === "number") {
      const numeric = Number(value);
      if (Number.isNaN(numeric)) {
        return { valid: false, error: "Please enter a valid number" };
      }
      if (validations.min !== undefined && numeric < validations.min) {
        return {
          valid: false,
          error: `Minimum allowed is ${validations.min}`,
        };
      }
      if (validations.max !== undefined && numeric > validations.max) {
        return {
          valid: false,
          error: `Maximum allowed is ${validations.max}`,
        };
      }
    }

    if (field?.input_type === "text" || field?.input_type === "textarea") {
      const trimmed = typeof value === "string" ? value.trim() : value;
      if (
        trimmed !== "" &&
        validations.min_length !== undefined &&
        trimmed.length < validations.min_length
      ) {
        return {
          valid: false,
          error:
            field?.field_key === "pan"
              ? "PAN must be 10 characters"
              : `Minimum length is ${validations.min_length}`,
        };
      }
      if (
        trimmed !== "" &&
        validations.max_length !== undefined &&
        trimmed.length > validations.max_length
      ) {
        return {
          valid: false,
          error:
            field?.field_key === "pan"
              ? "PAN must be 10 characters"
              : `Maximum length is ${validations.max_length}`,
        };
      }
    }

    const regexPattern = validationMeta.regex || validations.regex;
    if (regexPattern) {
      try {
        const pattern = new RegExp(regexPattern);
        if (!pattern.test(value)) {
          return {
            valid: false,
            error:
              field?.field_key === "pan"
                ? "Enter valid PAN (e.g., ABCDE1234F)"
                : validations.regex_message || "Invalid value",
          };
        }
      } catch (error) {
        console.warn("Invalid regex validation pattern:", regexPattern);
      }
    }
  }

  return { valid: true };
};

const isAnswerPresent = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

const buildCommittedFromDraft = (draft) => {
  const committed = {};
  Object.entries(draft || {}).forEach(([key, value]) => {
    if (isAnswerPresent(value)) {
      committed[key] = value;
    }
  });
  return committed;
};

const buildSegment = (questionsOrdered, cursorIndex, answersMap) => {
  if (!Array.isArray(questionsOrdered) || questionsOrdered.length === 0) {
    return {
      segmentType: null,
      segmentQuestions: [],
      nextCursorIndex: 0,
      startIndex: 0,
      allAnswered: true,
    };
  }

  let startIndex = cursorIndex ?? 0;
  while (
    startIndex < questionsOrdered.length &&
    isAnswerPresent(answersMap?.[questionsOrdered[startIndex]?.question_key])
  ) {
    startIndex += 1;
  }

  if (startIndex >= questionsOrdered.length) {
    return {
      segmentType: null,
      segmentQuestions: [],
      nextCursorIndex: startIndex,
      startIndex,
      allAnswered: true,
    };
  }

  const first = questionsOrdered[startIndex];
  if (first?.is_structural) {
    const answered = isAnswerPresent(answersMap?.[first?.question_key]);
    return {
      segmentType: "structural",
      segmentQuestions: [first],
      nextCursorIndex: startIndex + 1,
      startIndex,
      allAnswered: answered,
    };
  }

  let blockStart = startIndex;
  while (blockStart > 0 && !questionsOrdered[blockStart - 1]?.is_structural) {
    blockStart -= 1;
  }

  const segmentQuestions = [];
  let scanIndex = blockStart;
  while (
    scanIndex < questionsOrdered.length &&
    !questionsOrdered[scanIndex]?.is_structural
  ) {
    const question = questionsOrdered[scanIndex];
    if (question) {
      segmentQuestions.push(question);
    }
    scanIndex += 1;
  }

  const allAnswered = segmentQuestions.every((question) =>
    isAnswerPresent(answersMap?.[question.question_key])
  );

  return {
    segmentType: "normal",
    segmentQuestions,
    nextCursorIndex: scanIndex,
    startIndex: blockStart,
    allAnswered,
  };
};

const normalizePanValue = (field, rawValue) => {
  const validations = field?.validations || {};
  const validationMeta = validations.validation || {};
  let value = rawValue ?? "";
  if (validationMeta.trim) {
    value = typeof value === "string" ? value.trimStart() : value;
  }
  if (validationMeta.uppercase) {
    value = typeof value === "string" ? value.toUpperCase() : value;
  }
  if (
    validations.max_length !== undefined &&
    typeof value === "string" &&
    value.length > validations.max_length
  ) {
    value = value.slice(0, validations.max_length);
  }
  return value;
};

const normalizeGlobalValue = (question, rawValue) => {
  const validations = question?.validations || {};
  const validationMeta = validations.validation || {};
  let value = rawValue ?? "";
  if (validationMeta.trim) {
    value = typeof value === "string" ? value.trimStart() : value;
  }
  if (validationMeta.uppercase) {
    value = typeof value === "string" ? value.toUpperCase() : value;
  }
  if (validationMeta.numeric_only && typeof value === "string") {
    value = value.replace(/\D/g, "");
  }
  if (
    validations.max_length !== undefined &&
    typeof value === "string" &&
    value.length > validations.max_length
  ) {
    value = value.slice(0, validations.max_length);
  }
  return value;
};

const validateGlobalQuestion = (question, rawValue) => {
  if (!question) return { valid: true };
  const label = question?.question_text || question?.question_key || "This field";
  const validations = question?.validations || {};
  const validationMeta = validations.validation || {};
  const value =
    typeof rawValue === "string" ? rawValue : rawValue ?? "";

  if (question?.is_mandatory) {
    if (
      value === "" ||
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim() === "")
    ) {
      return { valid: false, error: `${label} is required` };
    }
  }

  if (value !== "" && value !== null && value !== undefined) {
    if (
      validationMeta.numeric_only &&
      typeof value === "string" &&
      /\D/.test(value)
    ) {
      return { valid: false, error: "Only digits are allowed" };
    }

    if (question?.input_type === "number") {
      const numeric = Number(value);
      if (Number.isNaN(numeric)) {
        return { valid: false, error: "Please enter a valid number" };
      }
      if (validations.min !== undefined && numeric < validations.min) {
        return {
          valid: false,
          error: `Minimum allowed is ${validations.min}`,
        };
      }
      if (validations.max !== undefined && numeric > validations.max) {
        return {
          valid: false,
          error: `Maximum allowed is ${validations.max}`,
        };
      }
    }

    if (question?.input_type === "date" && validations.max_date) {
      const maxDate =
        validations.max_date === "today"
          ? new Date().toISOString().slice(0, 10)
          : validations.max_date;
      if (typeof value === "string" && value > maxDate) {
        return {
          valid: false,
          error: `Date must be on or before ${maxDate}`,
        };
      }
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (
        trimmed !== "" &&
        validations.min_length !== undefined &&
        trimmed.length < validations.min_length
      ) {
        return {
          valid: false,
          error: `Minimum length is ${validations.min_length}`,
        };
      }
      if (
        trimmed !== "" &&
        validations.max_length !== undefined &&
        trimmed.length > validations.max_length
      ) {
        return {
          valid: false,
          error: `Maximum length is ${validations.max_length}`,
        };
      }
    }

    const regexPattern = validationMeta.regex;
    if (regexPattern) {
      try {
        const pattern = new RegExp(regexPattern);
        if (!pattern.test(value)) {
          return { valid: false, error: "Invalid value" };
        }
      } catch (error) {
        console.warn("Invalid regex validation pattern:", regexPattern);
      }
    }
  }

  return { valid: true };
};

const DeedFillPage = ({ isSidebarExpanded }) => {
  const { instance_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const instanceId = instance_id || location.state?.instanceId || "";
  const deedTypeId =
    location.state?.deed_type_id || searchParams.get("deed_type_id") || "";
  const [initialResponse, setInitialResponse] = useState(
    location.state?.initialResponse || null
  );
  const [initialLoading, setInitialLoading] = useState(false);
  const [initialError, setInitialError] = useState("");
  const [nextLoading, setNextLoading] = useState(false);
  const [nextError, setNextError] = useState("");
  const [partyInstances, setPartyInstances] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [partyFieldsLoading, setPartyFieldsLoading] = useState(false);
  const [partyFieldsError, setPartyFieldsError] = useState("");
  const [partyFields, setPartyFields] = useState([]);
  const [selectedPartyIndex, setSelectedPartyIndex] = useState(0);
  const [partyAnswersByInstanceId, setPartyAnswersByInstanceId] = useState({});
  const [partyFieldErrorsByInstanceId, setPartyFieldErrorsByInstanceId] = useState(
    {}
  );
  const [currentPartyInstanceId, setCurrentPartyInstanceId] = useState("");
  const [attemptedByInstanceId, setAttemptedByInstanceId] = useState({});
  const [step, setStep] = useState(1);
  const [submitPartyLoading, setSubmitPartyLoading] = useState(false);
  const [submitPartyError, setSubmitPartyError] = useState("");
  const [mainNextAttempted, setMainNextAttempted] = useState(false);
  const [globalQuestionsLoading, setGlobalQuestionsLoading] = useState(false);
  const [globalQuestionsError, setGlobalQuestionsError] = useState("");
  const [globalQuestions, setGlobalQuestions] = useState([]);
  // draftGlobalAnswers updates on every input change.
  // committedGlobalAnswers updates only on Save/Next to avoid disappearing-on-type.
  const [draftGlobalAnswers, setDraftGlobalAnswers] = useState({});
  const [committedGlobalAnswers, setCommittedGlobalAnswers] = useState({});
  const [step3CursorIndex, setStep3CursorIndex] = useState(0);
  const [touchedGlobal, setTouchedGlobal] = useState({});
  const [attemptedGlobalSubmit, setAttemptedGlobalSubmit] = useState(false);
  const [globalErrors, setGlobalErrors] = useState({});
  const [globalSubmitLoading, setGlobalSubmitLoading] = useState(false);
  const [globalSubmitError, setGlobalSubmitError] = useState("");
  const [highlightedClauseId, setHighlightedClauseId] = useState("");
  const clauseRefs = useRef({});
  const highlightTimeoutRef = useRef(null);
  const scrollDebounceRef = useRef(null);
  const patchPayloadLoggedRef = useRef(false);
  const answersByInstanceId = partyAnswersByInstanceId;
  const setAnswersByInstanceId = setPartyAnswersByInstanceId;
  const deedName = initialResponse?.deed?.name || "DEED";
  const deedTitle = useMemo(() => {
    if (!deedName || typeof deedName !== "string") return "DEED";
    return deedName.toUpperCase();
  }, [deedName]);

  const questionsOrdered = useMemo(() => {
    return [...globalQuestions].sort((a, b) => (a.order_no || 0) - (b.order_no || 0));
  }, [globalQuestions]);

  const previewGlobalAnswers = useMemo(() => {
    const next = {};
    questionsOrdered.forEach((question) => {
      if (!question?.preview_key) return;
      const value = draftGlobalAnswers?.[question.question_key];
      if (isAnswerPresent(value)) {
        next[question.preview_key] = value;
      }
    });
    return next;
  }, [draftGlobalAnswers, questionsOrdered]);

  const currentSegment = useMemo(
    () => buildSegment(questionsOrdered, step3CursorIndex, committedGlobalAnswers),
    [questionsOrdered, step3CursorIndex, committedGlobalAnswers]
  );

  useEffect(() => {
    if (currentSegment.segmentType !== "normal") return;
    if (!currentSegment.allAnswered) return;
    if (currentSegment.nextCursorIndex > step3CursorIndex) {
      setStep3CursorIndex(currentSegment.nextCursorIndex);
    }
  }, [currentSegment, step3CursorIndex]);

  useEffect(() => {
    if (initialResponse || !deedTypeId) return;

    const fetchInitial = async () => {
      setInitialLoading(true);
      setInitialError("");
      try {
        const res = await axiosInstance.get(`/api/${deedTypeId}/initial`);
        setInitialResponse(res.data);
      } catch (error) {
        console.error("Error fetching deed initial:", error);
        setInitialError("Unable to load questions. Please go back to Deeds and start again.");
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitial();
  }, [deedTypeId, initialResponse]);

  const partyGroups = useMemo(() => {
    const structure = Array.isArray(initialResponse?.structure)
      ? initialResponse.structure
      : Array.isArray(initialResponse?.data?.structure)
        ? initialResponse.data.structure
        : [];

    const map = new Map();
    structure.forEach((item) => {
      const group = item?.party_group;
      if (!group || !group.key) return;
      if (!map.has(group.key)) {
        map.set(group.key, group);
      }
    });
    return Array.from(map.values());
  }, [initialResponse]);

  const [partyCounts, setPartyCounts] = useState({});

  useEffect(() => {
    if (!partyGroups.length) return;
    setPartyCounts((prev) => {
      const next = { ...prev };
      partyGroups.forEach((group) => {
        if (next[group.key] === undefined) {
          next[group.key] = group.min_count ?? "";
        }
      });
      return next;
    });
  }, [partyGroups]);

  const handleCountChange = (key, value) => {
    setPartyCounts((prev) => ({ ...prev, [key]: value }));
  };

  const isNextDisabled = useMemo(() => {
    if (!partyGroups.length) return true;
    return partyGroups.some((group) => {
      const value = partyCounts[group.key];
      const min = group.min_count ?? 0;
      const max = group.max_count ?? 999;
      if (value === "" || value === undefined || value === null) return true;
      const numeric = Number(value);
      return Number.isNaN(numeric) || numeric < min || numeric > max;
    });
  }, [partyCounts, partyGroups]);

  const handleNext = async () => {
    if (!instanceId || isNextDisabled) return;

    setNextLoading(true);
    setNextError("");
    setPreviewError("");
    setPartyFieldsError("");

    const payload = {
      party_counts: partyGroups.reduce((acc, group) => {
        acc[group.key] = Number(partyCounts[group.key]);
        return acc;
      }, {}),
    };

    try {
      const res = await axiosInstance.patch(
        `/api/deed/${instanceId}/party-structure`,
        payload
      );
      const patchHasPartyInstances = !!res?.data?.party_instances;
      const refreshPreview = !!res?.data?.refresh_preview;
      if (patchHasPartyInstances) {
        setPartyInstances(res.data.party_instances);
      }
      if (refreshPreview) {
        setPreviewLoading(true);
        try {
          const previewRes = await axiosInstance.get(
            `/api/deed/preview/v4/${instanceId}`
          );
          setPreviewData(previewRes.data);
        } catch (error) {
          console.error("Error fetching deed preview:", error);
          setPreviewError("Failed to load preview.");
        } finally {
          setPreviewLoading(false);
        }
      }
      if (partyGroups.length > 0 && (patchHasPartyInstances || refreshPreview)) {
        setPartyFieldsLoading(true);
        try {
          const fieldsRes = await axiosInstance.get(
            `/api/deed/${instanceId}/party-fields`
          );
          const list = Array.isArray(fieldsRes.data?.party_fields)
            ? fieldsRes.data.party_fields
            : [];
          setPartyFields(list);
          setSelectedPartyIndex(0);
          setCurrentPartyInstanceId(list[0]?.party_instance_id || "");
          setPartyFieldErrorsByInstanceId({});
          setAttemptedByInstanceId({});
          setStep(2);
        } catch (error) {
          console.error("Error fetching party fields:", error);
          setPartyFieldsError("Failed to load party fields.");
        } finally {
          setPartyFieldsLoading(false);
        }
      }
    } catch (error) {
      console.error("Error updating party structure:", error);
      setNextError("Failed to save party structure. Please try again.");
    } finally {
      setNextLoading(false);
    }
  };

  const previewItems = useMemo(() => {
    if (previewData && Array.isArray(previewData?.clauses)) {
      return previewData.clauses;
    }
    const structure = Array.isArray(initialResponse?.structure)
      ? initialResponse.structure
      : Array.isArray(initialResponse?.data?.structure)
        ? initialResponse.data.structure
        : [];
    return structure;
  }, [initialResponse, previewData]);

  const selectedParty = partyFields[selectedPartyIndex] || null;
  const isLastParty = partyFields.length > 0 && selectedPartyIndex === partyFields.length - 1;

  useEffect(() => {
    setCurrentPartyInstanceId(selectedParty?.party_instance_id || "");
  }, [selectedParty?.party_instance_id]);

  const partyValidation = useMemo(
    () => validatePartyFields(partyFields, answersByInstanceId),
    [partyFields, answersByInstanceId]
  );

  const isAllPartyDataValid = useMemo(() => {
    if (!Array.isArray(partyFields) || partyFields.length === 0) return true;
    return partyFields.every((party) => {
      const fields = Array.isArray(party?.fields) ? party.fields : [];
      return fields.every((field) => {
        const rawValue =
          answersByInstanceId?.[party?.party_instance_id]?.[field?.field_key];
        const normalizedValue =
          field?.field_key === "pan" ? normalizePanValue(field, rawValue) : rawValue;
        const validation = validateField(field, normalizedValue);
        if (!validation.valid) return false;

        const validations = field?.validations || {};
        const validationMeta = validations.validation || {};

        if (
          validationMeta.numeric_only &&
          typeof normalizedValue === "string" &&
          /\D/.test(normalizedValue)
        ) {
          return false;
        }

        if (field?.input_type === "select") {
          const hasValue =
            normalizedValue !== "" &&
            normalizedValue !== null &&
            normalizedValue !== undefined;
          if (!hasValue) {
            return !field?.is_mandatory;
          }
          const options = Array.isArray(validations.options)
            ? validations.options
            : [];
          return options.some((opt) => opt?.value === normalizedValue);
        }

        return true;
      });
    });
  }, [partyFields, answersByInstanceId]);

  const isPartyComplete = (party) => {
    if (!party) return false;
    const fields = Array.isArray(party.fields) ? party.fields : [];
    return fields.every((field) => {
      if (!field?.is_mandatory) return true;
      const value = answersByInstanceId?.[party.party_instance_id]?.[field.field_key];
      if (field.input_type === "number") {
        return value !== "" && value !== undefined && value !== null;
      }
      return typeof value === "string" && value.trim().length > 0;
    });
  };

  const validatePartyInstance = (partyInstanceId, fields, answers) => {
    const errors = {};
    const instanceAnswers = answers?.[partyInstanceId] || {};
    const list = Array.isArray(fields) ? fields : [];

    list.forEach((field) => {
      const fieldKey = field?.field_key;
      if (!fieldKey) return;

      const rawValue = instanceAnswers[fieldKey];
      const normalizedValue =
        fieldKey === "pan" ? normalizePanValue(field, rawValue) : rawValue;
      const validation = validateField(field, normalizedValue);
      if (!validation.valid) {
        errors[fieldKey] = validation.error;
        return;
      }

      const validations = field?.validations || {};
      const validationMeta = validations.validation || {};
      if (
        validationMeta.numeric_only &&
        typeof normalizedValue === "string" &&
        /\D/.test(normalizedValue)
      ) {
        errors[fieldKey] = "Only digits are allowed";
        return;
      }

      if (field?.input_type === "select") {
        const hasValue =
          normalizedValue !== "" &&
          normalizedValue !== null &&
          normalizedValue !== undefined;
        if (!hasValue) {
          if (field?.is_mandatory) {
            errors[fieldKey] = `${field?.field_label || fieldKey} is required`;
          }
          return;
        }

        const options = Array.isArray(validations.options)
          ? validations.options
          : [];
        if (!options.some((opt) => opt?.value === normalizedValue)) {
          errors[fieldKey] = "Please select a valid option";
        }
      }
    });

    return { isValid: Object.keys(errors).length === 0, errors };
  };

  const selectedPartyValidation = useMemo(() => {
    if (!selectedParty) return { isValid: false, errors: {} };
    return validatePartyInstance(
      selectedParty.party_instance_id,
      selectedParty.fields || [],
      answersByInstanceId
    );
  }, [answersByInstanceId, selectedParty]);
  const isSelectedPartyValid = selectedPartyValidation.isValid;
  const partyValidationTooltipSimple =
    "Please complete all mandatory fields to continue";

  const patchPartyAnswers = async (partyInstanceId) => {
    if (!instanceId || !partyInstanceId) return { success: false };
    setSubmitPartyLoading(true);
    setSubmitPartyError("");

    const payload = {
      party_instance_id: partyInstanceId,
      answers: answersByInstanceId?.[partyInstanceId] || {},
    };

    if (!patchPayloadLoggedRef.current) {
      console.log("PATCH party payload", payload);
      patchPayloadLoggedRef.current = true;
    }

    try {
      const res = await axiosInstance.patch(
        `/api/deed/${instanceId}/party-field-answers`,
        payload
      );
      if (res?.data?.success !== true) {
        setSubmitPartyError("Failed to save party details. Please try again.");
        return { success: false };
      }

      if (res?.data?.refresh_preview) {
        setPreviewLoading(true);
        try {
          const previewRes = await axiosInstance.get(
            `/api/deed/preview/v4/${instanceId}`
          );
          setPreviewData(previewRes.data);
        } catch (error) {
          console.error("Error fetching deed preview:", error);
          setPreviewError("Failed to load preview.");
        } finally {
          setPreviewLoading(false);
        }
      }

      setPartyFieldErrorsByInstanceId((prev) => ({
        ...prev,
        [partyInstanceId]: {},
      }));
      return { success: true, data: res?.data };
    } catch (error) {
      const missingFields = error?.response?.data?.missing_fields;
      if (Array.isArray(missingFields) && missingFields.length > 0) {
        const backendErrors = missingFields.reduce((acc, key) => {
          acc[key] = "This field is required";
          return acc;
        }, {});
        setPartyFieldErrorsByInstanceId((prev) => ({
          ...prev,
          [partyInstanceId]: backendErrors,
        }));
      }
      setSubmitPartyError(
        error?.response?.data?.message ||
          "Failed to save party details. Please try again."
      );
      return { success: false, error };
    } finally {
      setSubmitPartyLoading(false);
    }
  };

  const validateAndSaveCurrentParty = async () => {
    const partyInstanceId =
      currentPartyInstanceId || selectedParty?.party_instance_id || "";
    if (!partyInstanceId) return { success: false };

    const currentParty = partyFields.find(
      (item) => item?.party_instance_id === partyInstanceId
    );
    if (!currentParty) return { success: false };

    setAttemptedByInstanceId((prev) => ({
      ...prev,
      [partyInstanceId]: true,
    }));

    const validation = validatePartyInstance(
      partyInstanceId,
      currentParty.fields || [],
      answersByInstanceId
    );

    if (!validation.isValid) {
      setPartyFieldErrorsByInstanceId((prev) => ({
        ...prev,
        [partyInstanceId]: validation.errors,
      }));
      return { success: false };
    }

    setPartyFieldErrorsByInstanceId((prev) => ({
      ...prev,
      [partyInstanceId]: {},
    }));
    return patchPartyAnswers(partyInstanceId);
  };

  const handlePartyTabSwitch = async (targetIndex) => {
    if (targetIndex === selectedPartyIndex) return;
    if (submitPartyLoading) return;

    const result = await validateAndSaveCurrentParty();
    if (!result.success) return;

    setSelectedPartyIndex(targetIndex);
  };

  const handleNextPerson = async () => {
    if (!selectedParty || submitPartyLoading) return;

    const result = await validateAndSaveCurrentParty();
    if (!result.success) return;

    setSelectedPartyIndex((prev) => Math.min(partyFields.length - 1, prev + 1));
  };

  const handleFieldChange = (partyInstanceId, partyGroupKey, fieldKey, value) => {
    const activePartyInstanceId = currentPartyInstanceId || partyInstanceId;
    if (!activePartyInstanceId || !fieldKey) return;

    setAnswersByInstanceId((prev) => ({
      ...prev,
      [activePartyInstanceId]: {
        ...(prev[activePartyInstanceId] || {}),
        [fieldKey]: value,
      },
    }));
    const placeholderKey = `${partyGroupKey}.${fieldKey}`;
    scrollToClause(placeholderKey, activePartyInstanceId);
  };

  const handleGlobalAnswerChange = (question, rawValue) => {
    if (!question) return;
    const value = normalizeGlobalValue(question, rawValue);
    setDraftGlobalAnswers((prev) => ({
      ...prev,
      [question.question_key]: value,
    }));
    const validation = validateGlobalQuestion(question, value);
    setGlobalErrors((prev) => ({
      ...prev,
      [question.question_key]: validation.valid ? "" : validation.error,
    }));
    if (question.preview_key) {
      scrollToClause(question.preview_key, null);
    }
  };

  const handleSubmitGlobalAnswers = async (payload) => {
    if (!instanceId) return;
    setGlobalSubmitLoading(true);
    setGlobalSubmitError("");
    try {
      const res = await axiosInstance.patch(
        `/api/deed/${instanceId}/global-answers`,
        payload
      );
      return res?.data;
    } catch (error) {
      console.error("Error saving global answers:", error);
      setGlobalSubmitError("Failed to save global answers. Please try again.");
      throw error;
    } finally {
      setGlobalSubmitLoading(false);
    }
  };

  const findClauseByPlaceholder = (clauses, placeholderKey, partyInstanceId) => {
    if (!Array.isArray(clauses) || !placeholderKey) return null;
    if (partyInstanceId) {
      const match = clauses.find(
        (clause) =>
          clause?.party_instance_id === partyInstanceId &&
          typeof clause?.text === "string" &&
          clause.text.includes(`[${placeholderKey}]`)
      );
      if (match) return match;
    }
    return clauses.find(
      (clause) =>
        typeof clause?.text === "string" &&
        clause.text.includes(`[${placeholderKey}]`)
    );
  };

  const scrollToClause = (placeholderKey, partyInstanceId) => {
    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current);
    }
    scrollDebounceRef.current = setTimeout(() => {
      const targetClause = findClauseByPlaceholder(
        previewItems,
        placeholderKey,
        partyInstanceId || null
      );
      if (!targetClause) return;
      const targetIndex = previewItems.indexOf(targetClause);
      const clauseId = `clause-${targetIndex}`;
      const element = clauseRefs.current[clauseId];
      if (element && element.scrollIntoView) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightedClauseId(clauseId);
        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current);
        }
        highlightTimeoutRef.current = setTimeout(() => {
          setHighlightedClauseId("");
        }, 1200);
      }
    }, 200);
  };

  const findFirstUnansweredIndex = (ordered) => {
    if (!Array.isArray(ordered) || ordered.length === 0) return 0;
    const idx = ordered.findIndex(
      (question) =>
        !isAnswerPresent(committedGlobalAnswers?.[question.question_key])
    );
    return idx >= 0 ? idx : ordered.length;
  };

  const loadGlobalQuestions = async ({
    resumeAfterRefetch = false,
    nextQuestionKey = "",
  } = {}) => {
    if (!instanceId) return;
    setGlobalQuestionsLoading(true);
    setGlobalQuestionsError("");
    try {
      const res = await axiosInstance.get(
        `/api/deed/${instanceId}/global-questions/v2`
      );
      const list = Array.isArray(res.data?.questions) ? res.data.questions : [];
      setGlobalQuestions(list);
      if (!resumeAfterRefetch) {
        setAttemptedGlobalSubmit(false);
        setTouchedGlobal({});
        setGlobalErrors({});
      }
      const ordered = [...list].sort((a, b) => (a.order_no || 0) - (b.order_no || 0));
      if (res?.data?.answers && typeof res.data.answers === "object") {
        setCommittedGlobalAnswers((prev) => ({ ...prev, ...res.data.answers }));
        setDraftGlobalAnswers((prev) => ({ ...prev, ...res.data.answers }));
      }
      let nextIndex = 0;
      if (resumeAfterRefetch && nextQuestionKey) {
        const idx = ordered.findIndex(
          (question) => question.question_key === nextQuestionKey
        );
        nextIndex = idx >= 0 ? idx : findFirstUnansweredIndex(ordered);
      } else {
        nextIndex = findFirstUnansweredIndex(ordered);
      }

      setStep3CursorIndex(nextIndex);
      setStep(3);
    } catch (error) {
      console.error("Error fetching global questions:", error);
      setGlobalQuestionsError("Failed to load global questions.");
    } finally {
      setGlobalQuestionsLoading(false);
    }
  };

  const handleMainNext = async () => {
    if (!isAllPartyDataValid) return;
    const result = await validateAndSaveCurrentParty();
    if (!result.success) return;
    await loadGlobalQuestions();
  };

  useEffect(() => {
    if (isAllPartyDataValid && mainNextAttempted) {
      setMainNextAttempted(false);
    }
  }, [isAllPartyDataValid, mainNextAttempted]);

  const handlePrevQuestion = () => {
    setStep(2);
  };

  const refreshPreview = async () => {
    setPreviewLoading(true);
    try {
      const previewRes = await axiosInstance.get(
        `/api/deed/preview/v4/${instanceId}`
      );
      setPreviewData(previewRes.data);
    } catch (error) {
      console.error("Error fetching deed preview:", error);
      setPreviewError("Failed to load preview.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const validateSegment = (segmentQuestions) => {
    const nextErrors = {};
    let isValid = true;
    segmentQuestions.forEach((question) => {
      const value = draftGlobalAnswers?.[question.question_key];
      const validation = validateGlobalQuestion(question, value);
      nextErrors[question.question_key] = validation.valid ? "" : validation.error;
      if (!validation.valid) {
        isValid = false;
      }
    });
    setGlobalErrors((prev) => ({ ...prev, ...nextErrors }));
    return isValid;
  };

  const buildSegmentAnswersPayload = (segmentQuestions) => {
    const answers = {};
    segmentQuestions.forEach((question) => {
      const value = draftGlobalAnswers?.[question.question_key];
      if (isAnswerPresent(value)) {
        answers[question.question_key] = value;
      }
    });
    return answers;
  };

  const handleSubmitStructuralSegment = async () => {
    const question = currentSegment.segmentQuestions[0];
    if (!question) return;
    setAttemptedGlobalSubmit(true);
    const isValid = validateSegment([question]);
    if (!isValid) return;
    const answers = buildSegmentAnswersPayload([question]);
    const nextQuestionKey =
      questionsOrdered[currentSegment.nextCursorIndex]?.question_key || "";
    try {
      const data = await handleSubmitGlobalAnswers({ answers });
      const mergedAnswers =
        data?.answers && typeof data.answers === "object"
          ? { ...answers, ...data.answers }
          : answers;
      const committedDraft = buildCommittedFromDraft(draftGlobalAnswers);
      setCommittedGlobalAnswers((prev) => ({
        ...prev,
        ...committedDraft,
        ...mergedAnswers,
      }));
      setDraftGlobalAnswers((prev) => ({ ...prev, ...mergedAnswers }));
      setAttemptedGlobalSubmit(false);
      setTouchedGlobal({});
      setGlobalErrors({});
      if (currentSegment.nextCursorIndex >= questionsOrdered.length) {
        navigate(`/deeds/${instanceId}/final`);
        return;
      }
      if (data?.refresh_preview) {
        await refreshPreview();
        await loadGlobalQuestions({
          resumeAfterRefetch: true,
          nextQuestionKey,
        });
      } else {
        setStep3CursorIndex(currentSegment.nextCursorIndex);
      }
    } catch (error) {
      console.error("Error saving structural question:", error);
    }
  };

  const handleSubmitNormalSegment = async () => {
    const segmentQuestions = currentSegment.segmentQuestions;
    if (!segmentQuestions.length) return;
    setAttemptedGlobalSubmit(true);
    const isValid = validateSegment(segmentQuestions);
    if (!isValid) return;
    const answers = buildSegmentAnswersPayload(segmentQuestions);
    const nextQuestionKey =
      questionsOrdered[currentSegment.nextCursorIndex]?.question_key || "";
    try {
      const data = await handleSubmitGlobalAnswers({ answers });
      const mergedAnswers =
        data?.answers && typeof data.answers === "object"
          ? { ...answers, ...data.answers }
          : answers;
      const committedDraft = buildCommittedFromDraft(draftGlobalAnswers);
      setCommittedGlobalAnswers((prev) => ({
        ...prev,
        ...committedDraft,
        ...mergedAnswers,
      }));
      setDraftGlobalAnswers((prev) => ({ ...prev, ...mergedAnswers }));
      setAttemptedGlobalSubmit(false);
      setTouchedGlobal({});
      setGlobalErrors({});
      if (currentSegment.nextCursorIndex >= questionsOrdered.length) {
        navigate(`/deeds/${instanceId}/final`);
        return;
      }
      if (data?.refresh_preview) {
        await refreshPreview();
        await loadGlobalQuestions({
          resumeAfterRefetch: true,
          nextQuestionKey,
        });
      } else {
        setStep3CursorIndex(currentSegment.nextCursorIndex);
      }
    } catch (error) {
      console.error("Error saving global answers:", error);
    }
  };

  const isLastSegment =
    !!currentSegment.segmentType &&
    currentSegment.nextCursorIndex >= questionsOrdered.length;

  return (
    <div
      className={`deeds-container deeds-fill-container ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="deeds-main">
        <div className="table-header">
          <div className="table-header-content">
            <div className="table-left-side">
              <h2 className="jost-bold">Deed Questions</h2>
            </div>

            <div className="table-right-side">
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => navigate("/deeds")}
              >
                Back to Deeds
              </button>
            </div>
          </div>
        </div>

        {!instanceId ? (
          <div className="deeds-error">
            <span>Missing deed instance. Please start the deed again.</span>
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => navigate("/deeds")}
            >
              Back to Deeds
            </button>
          </div>
        ) : (
          <div className="deeds-fill-grid deed-step-layout">
            <div className="deeds-fill-panel">
              <div className="deeds-panel-title">Party Involved</div>
              <div className="deeds-fill-panel-body">
                {step === 1 && (
                  <>
                  {initialLoading && (
                    <div className="deeds-muted">Loading questions...</div>
                  )}
                  {!initialLoading && initialError && (
                    <div className="deeds-error">
                      <span>{initialError}</span>
                      <button
                        className="btn btn-primary"
                        type="button"
                        onClick={() => navigate("/deeds")}
                      >
                        Back to Deeds
                      </button>
                    </div>
                  )}

                  {!initialLoading && !initialError && !partyGroups.length && (
                    <div className="deeds-muted">
                      Unable to load questions. Please go back to Deeds and start again.
                    </div>
                  )}

                  {!initialLoading && !initialError && partyGroups.length > 0 && (
                    <div className="deed-step-card">
                      <div className="deed-step-body">
                        <div className="deeds-question-list">
                          {partyGroups.map((group) => {
                            const value = partyCounts[group.key] ?? "";
                            const min = group.min_count ?? 0;
                            const max = group.max_count ?? 999;
                            const invalid =
                              value !== "" &&
                              (Number(value) < Number(min) || Number(value) > Number(max));

                            return (
                              <div className="deeds-question" key={group.key}>
                                <label className="deeds-question-label">
                                  How many {group.label}?
                                </label>
                                <input
                                  type="number"
                                  min={min}
                                  max={max}
                                  value={value}
                                  onChange={(e) =>
                                    handleCountChange(group.key, e.target.value)
                                  }
                                  className={`deeds-question-input ${invalid ? "invalid" : ""}`}
                                />
                                <div className="deeds-question-hint">
                                  Min {min}, Max {max}
                                </div>
                                {invalid && (
                                  <div className="deeds-question-error">
                                    Enter a value between {min} and {max}.
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {nextError && (
                            <div className="deeds-question-error">{nextError}</div>
                          )}
                        </div>
                      </div>
                      <div className="deed-step-footer">
                        <div className="deed-step-actions">
                          <button
                            className="btn btn-primary"
                            type="button"
                            onClick={handleNext}
                            disabled={isNextDisabled || nextLoading || initialLoading}
                          >
                            {nextLoading ? "Saving..." : "Next"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  </>
                )}

                {step === 2 && (
                  <>
                  {partyFieldsLoading && (
                    <div className="deeds-muted">Loading party fields...</div>
                  )}
                  {!partyFieldsLoading && partyFieldsError && (
                    <div className="deeds-error">
                      <span>{partyFieldsError}</span>
                    </div>
                  )}

                  {!partyFieldsLoading &&
                    !partyFieldsError &&
                    partyFields.length > 0 && (
                      <div className="deed-step-card">
                        <div className="deed-step-body">
                          <div className="deeds-party-fields">
                        <div className="deeds-party-nav">
                          {partyFields.map((party, index) => {
                            const complete = isPartyComplete(party);
                            return (
                              <button
                                key={party.party_instance_id}
                                className={`deeds-party-tab ${
                                  index === selectedPartyIndex ? "active" : ""
                                }`}
                                type="button"
                                onClick={() => handlePartyTabSwitch(index)}
                              >
                                <span>{party.party_label}</span>
                                {complete && (
                                  <span className="deeds-party-check">✓</span>
                                )}
                              </button>
                            );
                          })}
                        </div>

                        {selectedParty && (
                          <div className="deeds-party-form">
                            <div className="deeds-party-title">
                              {selectedParty.party_label}
                            </div>
                            {(selectedParty.fields || []).map((field) => {
                              const fieldKey = `${selectedParty.party_instance_id}:${field.field_key}`;
                              const value =
                                answersByInstanceId?.[selectedParty.party_instance_id]?.[
                                  field.field_key
                                ] ?? "";
                              const required = !!field.is_mandatory;
                              const validations = field?.validations || {};
                              const isPanField = field?.field_key === "pan";
                              const liveValidation = validateField(field, value);
                              const attempted =
                                attemptedByInstanceId?.[
                                  selectedParty.party_instance_id
                                ];
                              const shouldShowError = attempted;
                              const savedError =
                                partyFieldErrorsByInstanceId?.[
                                  selectedParty.party_instance_id
                                ]?.[field.field_key] || "";
                              const fieldError =
                                shouldShowError && (savedError || !liveValidation.valid)
                                  ? savedError || liveValidation.error
                                  : "";
                              const handleBlur = () => {
                                if (isPanField) {
                                  const normalized = normalizePanValue(field, value);
                                  if (normalized !== value) {
                                    handleFieldChange(
                                      selectedParty.party_instance_id,
                                      selectedParty.party_group_key,
                                      field.field_key,
                                      normalized
                                    );
                                  }
                                }
                              };
                              return (
                                <div className="deeds-party-field" key={fieldKey}>
                                  <label className="deeds-question-label">
                                    {field.field_label}
                                    {required ? " *" : ""}
                                  </label>
                                  {field.input_type === "textarea" ? (
                                    <textarea
                                      className="deeds-question-input deeds-textarea"
                                      value={value}
                                      onChange={(e) =>
                                        handleFieldChange(
                                          selectedParty.party_instance_id,
                                          selectedParty.party_group_key,
                                          field.field_key,
                                          isPanField
                                            ? normalizePanValue(field, e.target.value)
                                            : e.target.value
                                        )
                                      }
                                      onBlur={handleBlur}
                                      placeholder={validations.placeholder || ""}
                                      minLength={
                                        validations.min_length !== undefined
                                          ? validations.min_length
                                          : undefined
                                      }
                                      maxLength={
                                        validations.max_length !== undefined
                                          ? validations.max_length
                                          : undefined
                                      }
                                    />
                                  ) : field.input_type === "select" ? (
                                    <select
                                      className="deeds-question-input"
                                      value={value}
                                      onChange={(e) =>
                                        handleFieldChange(
                                          selectedParty.party_instance_id,
                                          selectedParty.party_group_key,
                                          field.field_key,
                                          e.target.value
                                        )
                                      }
                                      onBlur={handleBlur}
                                    >
                                      <option value="" disabled>
                                        {validations.placeholder || "Select"}
                                      </option>
                                      {(validations.options || []).map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <input
                                      type={
                                        field.input_type === "number" ? "number" : "text"
                                      }
                                      className="deeds-question-input"
                                      value={value}
                                      onChange={(e) =>
                                        handleFieldChange(
                                          selectedParty.party_instance_id,
                                          selectedParty.party_group_key,
                                          field.field_key,
                                          isPanField
                                            ? normalizePanValue(field, e.target.value)
                                            : e.target.value
                                        )
                                      }
                                      onBlur={handleBlur}
                                      placeholder={validations.placeholder || ""}
                                      min={
                                        field.input_type === "number" &&
                                        validations.min !== undefined
                                          ? validations.min
                                          : undefined
                                      }
                                      max={
                                        field.input_type === "number" &&
                                        validations.max !== undefined
                                          ? validations.max
                                          : undefined
                                      }
                                      step={
                                        field.input_type === "number" &&
                                        validations.step !== undefined
                                          ? validations.step
                                          : undefined
                                      }
                                      minLength={
                                        field.input_type !== "number" &&
                                        validations.min_length !== undefined
                                          ? validations.min_length
                                          : undefined
                                      }
                                      maxLength={
                                        field.input_type !== "number" &&
                                        validations.max_length !== undefined
                                          ? validations.max_length
                                          : undefined
                                      }
                                    />
                                  )}
                                  {fieldError && (
                                    <div className="deeds-question-error">
                                      {fieldError}
                                    </div>
                                  )}
                                </div>
                              );
                            })}

                            {submitPartyError && (
                              <div className="deeds-question-error">
                                {submitPartyError}
                              </div>
                            )}
                          </div>
                        )}
                          </div>
                        </div>
                        <div className="deed-step-footer">
                          <div className="deed-step-actions">
                            <button
                              className="btn btn-secondary"
                              type="button"
                              onClick={() => setStep(1)}
                            >
                              Previous
                            </button>
                            {!isLastParty ? (
                              <Tooltip
                                title={partyValidationTooltipSimple}
                                arrow
                                disableHoverListener={
                                  !attemptedByInstanceId[selectedParty?.party_instance_id] ||
                                  isSelectedPartyValid
                                }
                              >
                                <span style={{ display: "inline-block" }}>
                                  <button
                                    className="btn btn-primary"
                                    type="button"
                                    onClick={handleNextPerson}
                                    disabled={submitPartyLoading}
                                  >
                                    Next Person
                                  </button>
                                </span>
                              </Tooltip>
                            ) : (
                              <div
                                className="deeds-next-wrapper"
                                onClick={() => {
                                  if (!isAllPartyDataValid) {
                                    setMainNextAttempted(true);
                                  }
                                }}
                              >
                                <button
                                  className={`btn btn-primary ${
                                    !isAllPartyDataValid ? "deeds-next-disabled" : ""
                                  }`}
                                  type="button"
                                  onClick={handleMainNext}
                                  disabled={!isAllPartyDataValid || submitPartyLoading}
                                >
                                  {submitPartyLoading ? "Saving..." : "Next"}
                                </button>
                                {!isAllPartyDataValid && (
                                  <div
                                    className={`deeds-next-tooltip ${
                                      mainNextAttempted ? "show" : ""
                                    }`}
                                  >
                                    Please fill all required party details to
                                    continue.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {step === 3 && (
                  <>
                  {globalQuestionsLoading && (
                    <div className="deeds-muted">Loading global questions...</div>
                  )}
                  {!globalQuestionsLoading && globalQuestionsError && (
                    <div className="deeds-error">
                      <span>{globalQuestionsError}</span>
                    </div>
                  )}
                  {!globalQuestionsLoading &&
                    !globalQuestionsError &&
                    currentSegment.segmentType && (
                      <div className="deed-step-card">
                        <div className="deed-step-body">
                          <div className="deeds-question-list">
                            {currentSegment.segmentQuestions.map((question) => {
                              const value =
                                draftGlobalAnswers?.[question.question_key] ?? "";
                              const validations = question?.validations || {};
                              const shouldShowError =
                                attemptedGlobalSubmit ||
                                touchedGlobal?.[question.question_key];
                              const error = globalErrors?.[question.question_key];
                              return (
                                <div className="deeds-question" key={question.question_key}>
                                  <label className="deeds-question-label">
                                    {question.question_text || question.question_key}
                                    {question.is_mandatory ? " *" : ""}
                                  </label>
                                  {/* Keep answered questions in the current block visible;
                                      hide/show is driven by segmentation, not by answers. */}
                                  {Array.isArray(question?.validations?.options) &&
                                  question.validations.options.length > 0 ? (
                                    <select
                                      className="deeds-question-input"
                                      value={value}
                                      onChange={(e) =>
                                        handleGlobalAnswerChange(
                                          question,
                                          e.target.value
                                        )
                                      }
                                      onBlur={() =>
                                        setTouchedGlobal((prev) => {
                                          const next = {
                                            ...prev,
                                            [question.question_key]: true,
                                          };
                                          const validation = validateGlobalQuestion(
                                            question,
                                            value
                                          );
                                          setGlobalErrors((errorsPrev) => ({
                                            ...errorsPrev,
                                            [question.question_key]: validation.valid
                                              ? ""
                                              : validation.error,
                                          }));
                                          return next;
                                        })
                                      }
                                    >
                                      <option value="" disabled>
                                        {validations.placeholder || "Select"}
                                      </option>
                                      {validations.options.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </select>
                                  ) : question.input_type === "textarea" ? (
                                    <textarea
                                      className="deeds-question-input deeds-textarea"
                                      value={value}
                                      onChange={(e) =>
                                        handleGlobalAnswerChange(
                                          question,
                                          e.target.value
                                        )
                                      }
                                      onBlur={() =>
                                        setTouchedGlobal((prev) => {
                                          const next = {
                                            ...prev,
                                            [question.question_key]: true,
                                          };
                                          const validation = validateGlobalQuestion(
                                            question,
                                            value
                                          );
                                          setGlobalErrors((errorsPrev) => ({
                                            ...errorsPrev,
                                            [question.question_key]: validation.valid
                                              ? ""
                                              : validation.error,
                                          }));
                                          return next;
                                        })
                                      }
                                      placeholder={validations?.placeholder || ""}
                                      maxLength={
                                        validations?.max_length !== undefined
                                          ? validations.max_length
                                          : undefined
                                      }
                                    />
                                  ) : question.input_type === "select_month" ? (
                                    <select
                                      className="deeds-question-input"
                                      value={value}
                                      onChange={(e) =>
                                        handleGlobalAnswerChange(
                                          question,
                                          e.target.value
                                        )
                                      }
                                      onBlur={() =>
                                        setTouchedGlobal((prev) => {
                                          const next = {
                                            ...prev,
                                            [question.question_key]: true,
                                          };
                                          const validation = validateGlobalQuestion(
                                            question,
                                            value
                                          );
                                          setGlobalErrors((errorsPrev) => ({
                                            ...errorsPrev,
                                            [question.question_key]: validation.valid
                                              ? ""
                                              : validation.error,
                                          }));
                                          return next;
                                        })
                                      }
                                    >
                                      <option value="">Select month</option>
                                      {[
                                        "January",
                                        "February",
                                        "March",
                                        "April",
                                        "May",
                                        "June",
                                        "July",
                                        "August",
                                        "September",
                                        "October",
                                        "November",
                                        "December",
                                      ].map((month) => (
                                        <option key={month} value={month}>
                                          {month}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <input
                                      type={
                                        question.input_type === "number"
                                          ? "number"
                                          : question.input_type === "date"
                                            ? "date"
                                            : question.input_type === "year"
                                              ? "number"
                                              : "text"
                                      }
                                      className="deeds-question-input"
                                      value={value}
                                      onChange={(e) =>
                                        handleGlobalAnswerChange(
                                          question,
                                          e.target.value
                                        )
                                      }
                                      onBlur={() =>
                                        setTouchedGlobal((prev) => {
                                          const next = {
                                            ...prev,
                                            [question.question_key]: true,
                                          };
                                          const validation = validateGlobalQuestion(
                                            question,
                                            value
                                          );
                                          setGlobalErrors((errorsPrev) => ({
                                            ...errorsPrev,
                                            [question.question_key]: validation.valid
                                              ? ""
                                              : validation.error,
                                          }));
                                          return next;
                                        })
                                      }
                                      placeholder={validations?.placeholder || ""}
                                      min={
                                        question.input_type === "number" &&
                                        validations?.min !== undefined
                                          ? validations.min
                                          : undefined
                                      }
                                      max={
                                        question.input_type === "number" &&
                                        validations?.max !== undefined
                                          ? validations.max
                                          : question.input_type === "date" &&
                                            validations?.max_date
                                            ? validations.max_date === "today"
                                              ? new Date().toISOString().slice(0, 10)
                                              : validations.max_date
                                            : undefined
                                      }
                                      step={
                                        question.input_type === "number" &&
                                        validations?.step !== undefined
                                          ? validations.step
                                          : question.input_type === "number"
                                            ? 1
                                            : undefined
                                      }
                                      maxLength={
                                        question.input_type !== "number" &&
                                        validations?.max_length !== undefined
                                          ? validations.max_length
                                          : undefined
                                      }
                                    />
                                  )}
                                  {shouldShowError && error && (
                                    <div className="deeds-question-error">{error}</div>
                                  )}
                                </div>
                              );
                            })}
                            {globalSubmitError && (
                              <div className="deeds-question-error">
                                {globalSubmitError}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="deed-step-footer">
                          <div className="deed-step-actions">
                            <button
                              className="btn btn-secondary"
                              type="button"
                              onClick={handlePrevQuestion}
                            >
                              Previous
                            </button>
                            <button
                              className="btn btn-primary"
                              type="button"
                              onClick={
                                currentSegment.segmentType === "structural"
                                  ? handleSubmitStructuralSegment
                                  : handleSubmitNormalSegment
                              }
                              disabled={globalSubmitLoading}
                            >
                              {globalSubmitLoading
                                ? "Saving..."
                                : isLastSegment
                                  ? "Save and Finalize"
                                  : "Next"}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  {!globalQuestionsLoading &&
                    !globalQuestionsError &&
                    !currentSegment.segmentType && (
                      <div className="deeds-muted">
                        All questions are complete.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="deeds-fill-panel deeds-fill-preview">
              <div className="deeds-panel-title deeds-preview-header">
                <div className="deeds-preview-title-row">
                  <span className="deeds-live-dot" aria-hidden="true" />
                  <span>Live Preview</span>
                  <span className="deeds-preview-kicker">
                    Updates as you fill the deed details
                  </span>
                </div>
              </div>
              <div className="deeds-fill-panel-body">
                {(initialLoading || previewLoading) && (
                  <div className="deeds-muted">Loading preview...</div>
                )}
                {!initialLoading && !previewLoading && initialError && (
                  <div className="deeds-error">
                    <span>{initialError}</span>
                  </div>
                )}
                {!initialLoading && !previewLoading && previewError && (
                  <div className="deeds-error">
                    <span>{previewError}</span>
                  </div>
                )}
                {!initialLoading && !previewLoading && !initialError && !previewError && (
                  <DeedPreviewRenderer
                    deedTitle={deedTitle}
                    previewItems={previewItems}
                    highlightedClauseId={highlightedClauseId}
                    clauseRefs={clauseRefs}
                    answersByInstanceId={answersByInstanceId}
                    previewGlobalAnswers={previewGlobalAnswers}
                    containerClassName="deeds-preview-content"
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeedFillPage;
